import {
  takeEvery,
  put,
  select,
  call,
  SagaReturnType,
} from 'redux-saga/effects';
import { selectBearerToken } from '../selectors/user';
import { actions, LicensingFeatures } from '../slices/app';
import { toErrorWithMessage } from '@/helpers/Errors';
import HttpProvider from '@/helpers/HttpProvider';
import { isMinimumVersion } from '@/utils/version';

export const AppSagas = [
  takeEvery('user/LOGIN_COMPLETE', doGetBlocks),
  takeEvery('user/LOGIN_COMPLETE', fetchLicensingFeatures),
];

async function fetchBlockPermissions(path: string, bearerToken: string) {
  const [err, blockPermissions] = await HttpProvider.get<{
    authorized: boolean;
  }>(
    `${path}/api/management/projects/${window.ContensisProjectApiId}/security/permissions/blocks/actions/view`,
    {
      headers: { Authorization: `Bearer ${bearerToken}` },
    }
  );

  if (err)
    console.log('[FetchBlockPermissions] -->', toErrorWithMessage(err).message);

  return !!blockPermissions?.authorized;
}

async function doFetchBlocksAPI(
  path: string,
  endpoint: string,
  bearerToken: string
) {
  const [err, blocks] = await HttpProvider.get(`${path}/${endpoint}`, {
    headers: { Authorization: `Bearer ${bearerToken}` },
  });

  if (err) {
    const { message } = toErrorWithMessage(err);
    console.error(`[FetchBlocks] --> ${message}`);
    return { error: message, blocks: [] };
  }

  if (Array.isArray(blocks) && blocks.length > 0) return { blocks };
  else return { blocks: [] };
}

function* doGetBlocks() {
  yield put(actions.SET_BLOCKS_LOADING());

  const bearerToken: string = yield select(selectBearerToken);
  const path = window?.location?.origin;
  const endpoint = 'api/preview-toolbar/blocks';

  const hasPermissions: boolean = yield call(
    fetchBlockPermissions,
    path,
    bearerToken
  );
  if (hasPermissions) {
    try {
      const { blocks, error }: SagaReturnType<typeof doFetchBlocksAPI> =
        yield call(doFetchBlocksAPI, path, endpoint, bearerToken);
      if (error) {
        yield put(actions.SET_BLOCKS_ERROR({ error }));
      } else {
        yield put(actions.SET_BLOCKS_PAYLOAD({ blocks }));
      }
    } catch (e) {
      const { message } = toErrorWithMessage(e);
      console.error(`[ErrorOccurred] ${message}`);
      yield put(actions.SET_BLOCKS_ERROR({ error: message }));
    }
  } else {
    yield put(actions.SET_BLOCKS_ERROR({ error: 'Invalid permission' }));
  }
}

function* fetchLicensingFeatures() {
  // Don't fetch licensing features for versions below 17.1
  if (isMinimumVersion('17.1')) {
    const bearerToken: string = yield select(selectBearerToken);

    try {
      yield put(actions.SET_FEATURES_LOADING());
      const [err, features]: SagaReturnType<
        typeof HttpProvider.get<LicensingFeatures>
      > = yield HttpProvider.get(
        `https://cms-${window.ContensisAlias}.cloud.contensis.com/api/licensing/features`,
        {
          headers: { Authorization: `Bearer ${bearerToken}` },
        }
      );

      if (features) {
        yield put(actions.SET_FEATURES_COMPLETE({ features }));
      } else {
        yield put(
          actions.SET_FEATURES_ERROR({ error: toErrorWithMessage(err).message })
        );
        throw err;
      }
    } catch (e) {
      console.error(
        `[FetchLicensingFeatures] ${toErrorWithMessage(e).message}`
      );
    }
  }
}
